"use client";

import { useEffect, useRef, useState } from "react";

export const SPINNER_VARIANTS = [
  "magnify",
  "loop",
  "breathe",
  "left-to-right",
  "helix",
  "strip",
  "snake",
  "square",
  "arrow-spin",
  "wave",
  "pulse",
  "down-up",
] as const;

export type SpinnerVariant = (typeof SPINNER_VARIANTS)[number];

const SPINNER_FILE_MAP: Record<SpinnerVariant, string> = {
  magnify: "1 - Hero - Magnify.json",
  loop: "2 - The problem - Loop.json",
  breathe: "3 - Breathe.json",
  "left-to-right": "4 - Left to right.json",
  helix: "5 - Helix.json",
  strip: "6 - Strip.json",
  snake: "7 - Snake.json",
  square: "8 - Square.json",
  "arrow-spin": "9 - Arrow-Spin.json",
  wave: "10 - Wave.json",
  pulse: "11 - Pulse.json",
  "down-up": "12 - Down Up.json",
};

interface SpinnerData {
  fps: number;
  frames: number[][][];
}

interface BrailleSpinnerProps {
  variant?: SpinnerVariant;
  size?: number;
  fps?: number;
  dotScale?: number;
  color?: string;
  className?: string;
}

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

const spinnerCache = new Map<SpinnerVariant, SpinnerData>();

export default function BrailleSpinner({
  variant = "magnify",
  size = 24,
  fps: fpsProp,
  dotScale = 0.38,
  color = "#FF5500",
  className,
}: BrailleSpinnerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [spinnerData, setSpinnerData] = useState<SpinnerData | null>(
    spinnerCache.get(variant) ?? null,
  );
  // Pause the rAF loop when the spinner is off-screen — main perf win
  // since multiple chips animate simultaneously across the page.
  const [isVisible, setIsVisible] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    if (spinnerCache.has(variant)) {
      setSpinnerData(spinnerCache.get(variant)!);
      return;
    }

    const fileName = SPINNER_FILE_MAP[variant];
    fetch(`/animations/spinners/${encodeURIComponent(fileName)}`)
      .then((res) => res.json())
      .then((data) => {
        const parsed: SpinnerData = { fps: data.fps, frames: data.frames };
        spinnerCache.set(variant, parsed);
        setSpinnerData(parsed);
      })
      .catch((err) => {
        console.error(`Failed to load spinner "${variant}":`, err);
      });
  }, [variant]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { rootMargin: "100px" },
    );
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !spinnerData) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const fps = fpsProp ?? spinnerData.fps;
    const frames = spinnerData.frames;
    const [r, g, b] = hexToRgb(color);
    const colorStr = `rgb(${r},${g},${b})`;

    const w = canvas.width;
    const h = canvas.height;
    const dw = w / 8;
    const dh = h / 8;
    const rMax = Math.min(dw, dh) * dotScale;

    // Set fillStyle once; vary per-dot opacity via globalAlpha.
    // Avoids 64 string allocations + CSS-color parses per frame.
    ctx.fillStyle = colorStr;

    const drawFrame = (dA: number[][], dB: number[][], t: number) => {
      ctx.clearRect(0, 0, w, h);
      for (let row = 0; row < 8; row++) {
        const rowA = dA[row];
        const rowB = dB[row];
        for (let col = 0; col < 8; col++) {
          const op = rowA[col] + (rowB[col] - rowA[col]) * t;
          if (op <= 0.01) continue;
          ctx.globalAlpha = op;
          ctx.beginPath();
          ctx.arc((col + 0.5) * dw, (row + 0.5) * dh, rMax * op, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    };

    // Reduced motion or off-screen: render frame 0 once, no rAF.
    if (reduceMotion || !isVisible) {
      drawFrame(frames[0], frames[0], 0);
      return;
    }

    let startTime: number | null = null;
    let rafId: number;

    function draw(ts: number) {
      if (!startTime) startTime = ts;

      const msPerFrame = 1000 / fps;
      const rawFrame =
        ((ts - startTime) % (frames.length * msPerFrame)) / msPerFrame;
      const fA = Math.floor(rawFrame) % frames.length;
      const fB = (fA + 1) % frames.length;
      const t = easeInOut(rawFrame - Math.floor(rawFrame));

      drawFrame(frames[fA], frames[fB], t);

      rafId = requestAnimationFrame(draw);
    }

    rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [fpsProp, dotScale, color, spinnerData, isVisible, reduceMotion]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      className={className}
    />
  );
}
